import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useOnInView } from 'react-intersection-observer'
import {
  myProjectSortDirectionOptions,
  myProjectSortOptions,
  useGetMyProjectsQuery
} from '../../../../react-query/project/query/use-get-my-projects-query'
import ListItemComponent from '../../../../components/stories/molecules/list-items'
import { listItemListViewVariantOptions } from '../../../../components/stories/molecules/list-items/constants'
import {
  HeaderTitle,
  LoadingNextPage,
  MobileListContainer,
  MobileViewContainer,
  NotFoundContainerMobile
} from '../../styled-component'
import { SpinnerGrey } from '../../../../components/stories/atoms/icons/special'
import { iconSizeOptions } from '../../../../components/stories/atoms/icons/icons-sizes'
import ProjectMenuMobileLoadList from './components/project-menu-mobile-load-list'
import { Project } from '../../../../components/stories/atoms/icons/entities'
import { FormattedMessage, useIntl } from 'react-intl'
import ProjectMenuMbileBadgeRole from './components/project-menu-mobile-badge-role'
import { formatRelativeDate } from '../../../../utils/format-relative-date'
import ProjectMenuMobileSearch from './components/project-menu-mobile-search'
import { isEmpty } from 'lodash'
import NotFoundComponent from '../../../../components/stories/molecules/not-found'
import colorsOptions from '../../../../components/stories/fundamentals/colors-styles/constants'
import { useHistory } from 'react-router-dom'
import { textStyleSizeOptions } from '../../../../components/stories/fundamentals/text-styles/constants'

const ProjectMenuMobile = () => {
  const [search, setSearch] = useState('')
  const [isSticky, setIsSticky] = useState(false)
  const containerRef = useRef(null)
  const sentinelRef = useRef(null)
  const { locale, formatMessage } = useIntl()
  const history = useHistory()

  const queryParams = useMemo(
    () => ({
      search,
      sortDirection: myProjectSortDirectionOptions.DESC,
      projectSort: myProjectSortOptions.LAST_MODIFIED
    }),
    [search]
  )

  const {
    data: myProjectList,
    isLoading: myProjectListLoading,
    isFetchingNextPage: nextPageIsLoading,
    fetchNextPage,
    hasNextPage
  } = useGetMyProjectsQuery({ params: queryParams })

  // infinite scroll
  const onBottomReached = useCallback(() => {
    if (!hasNextPage || nextPageIsLoading) return
    fetchNextPage()
  }, [hasNextPage, nextPageIsLoading])

  const onBottomReachedRef = useRef(onBottomReached)

  const bottomRef = useOnInView(
    useCallback((inView) => {
      if (inView) onBottomReachedRef.current?.()
    }, []),
    { rootMargin: '20px', skip: !hasNextPage }
  )

  useEffect(() => {
    onBottomReachedRef.current = onBottomReached
  }, [onBottomReached])

  // sticky header

  useEffect(() => {
    const sentinel = sentinelRef.current
    const container = containerRef.current
    if (!sentinel || !container) return

    const observer = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      { root: container, threshold: 0 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  // helpers

  const projectList = useMemo(
    () => myProjectList?.pages.flatMap((p) => p.content) ?? [],
    [myProjectList]
  )

  // derived states
  const isEmptySearch = isEmpty(projectList) && !myProjectListLoading && search
  const isEmptyList = isEmpty(projectList) && !myProjectListLoading && !search

  // handlers

  const handleOpenProject = (id) => {
    history.push(`/project/${id}`)
  }

  // render helpers
  const EmptyList = () => (
    <NotFoundContainerMobile>
      <NotFoundComponent
        icon={
          <Project
            style={{ width: '144px', height: '144px' }}
            stroke={colorsOptions.greys['grey-50']}
          />
        }
        header={formatMessage({ id: 'no_projects_yet' })}
        headerSize={textStyleSizeOptions['TEXT-XXL']}
        description={formatMessage({
          id: 'no_projects_yet_description_permission'
        })}
        descriptionSize={textStyleSizeOptions['TEXT-L']}
      />
    </NotFoundContainerMobile>
  )

  const EmptySearch = () => (
    <NotFoundContainerMobile>
      <NotFoundComponent
        header={formatMessage({ id: 'no_result_found' })}
        description={formatMessage({ id: 'search_list_empty_state_subtitle' })}
      />
    </NotFoundContainerMobile>
  )

  const MobileList = () => (
    <MobileListContainer>
      <ListItemComponent.Root
        listViewType={listItemListViewVariantOptions.SEPARATE}
      >
        {projectList.map((item) => (
          <ListItemComponent.Item
            itemKey={item.id}
            key={item.id}
            id={<ProjectMenuMbileBadgeRole item={item} />}
            leadingIcon={<Project size="icon-l" />}
            title={item.name}
            secondaryText={item.organisationName}
            date={formatRelativeDate(item.lastActivity, locale, formatMessage)}
            onClick={() => handleOpenProject(item.id)}
          />
        ))}
      </ListItemComponent.Root>

      <div ref={bottomRef} style={{ height: 1 }} />

      {nextPageIsLoading && (
        <LoadingNextPage style={{ paddingBottom: '0' }}>
          <SpinnerGrey size={iconSizeOptions['ICON-XL']} />
        </LoadingNextPage>
      )}
    </MobileListContainer>
  )

  const DefineContent = () => {
    if (isEmptyList) return <EmptyList />
    if (isEmptySearch) return <EmptySearch />
    return <MobileList />
  }

  return (
    <MobileViewContainer ref={containerRef}>
      <div ref={sentinelRef} style={{ height: 1 }} />
      <HeaderTitle $isSticky={isSticky}>
        <FormattedMessage id="projects_MyProjects" />
      </HeaderTitle>
      <ProjectMenuMobileSearch setSearch={setSearch} />

      <ProjectMenuMobileLoadList isLoading={myProjectListLoading}>
        <DefineContent />
      </ProjectMenuMobileLoadList>
    </MobileViewContainer>
  )
}

export default ProjectMenuMobile
